import {
	type FanTokenOfferingBuyOrderStatus,
	type FanTokenPurchaseStatusEnum,
	type LabelTypeColors,
	type PaymentPaymentStatus,
	type TranslationIDs,
	useIntl
} from '@capsicum/common';
import { useId } from 'react';
import styled from 'styled-components';

import { Divider } from '../../atoms/divider/Divider';
import { Badge } from '../badge/Badge';

export type StatusInformationsProps = {
	reference?: string;
	status: FanTokenOfferingBuyOrderStatus | FanTokenPurchaseStatusEnum | PaymentPaymentStatus;
};

/**
 * StatusInformation component
 *
 * @param {StyleProp<TextStyle>} reference Reference number displayed
 * @param {Status} status Status type will automatically change the color of the badge and the translation linked
 *
 * @returns StatusInformations
 */
export const StatusInformations = ({ reference, status = 'created', ...props }: StatusInformationsProps) => {
	const labelID = useId();
	const { $t } = useIntl();
	const { color, translatedMessage } = StatusBadgeGenerator(status);

	const referenceLabel = $t('payments.label.reference');

	const statusLabel = $t('payments.label.status');

	return (
		<S.Wrapper {...props}>
			{reference && (
				<>
					<S.FlexWrapper>
						<S.TextColumnLeft>{referenceLabel}</S.TextColumnLeft>
						<S.TextColumnRight aria-label="Reference Number" id="Reference Number">
							{reference}
						</S.TextColumnRight>
					</S.FlexWrapper>
					<Divider orientation="horizontal" />
				</>
			)}
			<S.FlexWrapper>
				<S.TextColumnLeft id={labelID}>{statusLabel}</S.TextColumnLeft>
				<S.TextColumnRight>
					<S.FlexWrapper>
						<S.BadgeWrapper aria-label={`Badge ${color}`} id={`Status ${color}`}>
							<Badge variant={color} />
						</S.BadgeWrapper>
						<span aria-label="Status Result" aria-labelledby={labelID} id="Status Result">
							{translatedMessage}
						</span>
					</S.FlexWrapper>
				</S.TextColumnRight>
			</S.FlexWrapper>
		</S.Wrapper>
	);
};

const statusTextsAndColors: Record<StatusInformationsProps['status'], { color: LabelTypeColors; id: TranslationIDs }> =
	{
		accepted: {
			color: 'green',
			id: 'payments.status.processing'
		},
		cancelled: {
			color: 'red',
			id: 'payments.status.cancelled'
		},
		cancelling: {
			color: 'orange',
			id: 'payments.status.cancelled'
		},
		chargedBack: {
			color: 'red',
			id: 'payments.status.chargedBack'
		},
		completed: {
			color: 'green',
			id: 'payments.status.completed'
		},
		created: {
			color: 'green',
			id: 'payments.status.created'
		},
		error: {
			color: 'red',
			id: 'payments.status.failed'
		},
		failed: {
			color: 'red',
			id: 'payments.status.failed'
		},
		processing: {
			color: 'orange',
			id: 'payments.status.processing'
		},
		rejected: {
			color: 'red',
			id: 'payments.status.rejected'
		},
		waiting: {
			color: 'orange',
			id: 'payments.status.processing'
		}
	};

export const StatusBadgeGenerator = (status: StatusInformationsProps['status']) => {
	const { $t } = useIntl();
	const config = statusTextsAndColors[status];

	// Do not fail if status is not in the enum,
	// just use the processing status with orange.
	return config
		? {
				color: config.color,
				translatedMessage: $t(config.id)
			}
		: {
				color: 'orange' as LabelTypeColors,
				translatedMessage: 'payments.status.processing'
			};
};
namespace S {
	export const Wrapper = styled.div`
		${({ theme }) => `
			background-color: ${theme.colors.backgrounds.main.screen};
			padding: ${theme.sizing.small};
			gap: ${theme.spacings.xxsmall};;
		`};
		${({ theme }) => theme.border.radius.medium};
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 366px;
		justify-content: center;
	`;

	export const FlexWrapper = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
	`;

	export const BadgeWrapper = styled.div`
		${({ theme }) => `
			padding-right: ${theme.spacings.xsmall};
			padding-left: ${theme.spacings.xsmall};
		`};
	`;

	export const TextColumnLeft = styled.div`
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const TextColumnRight = styled.div`
		${({ theme }) => theme.fonts.text.text1};
	`;
}
